import { createSlice } from '@reduxjs/toolkit';
import { t } from 'i18next';
import { showSuccess } from '../../app/utils/sweetAlert';
import {
	addCity,
	addCountry,
	addRegion,
	cityStatus,
	countryStatus,
	deleteCity,
	deleteCountry,
	deleteRegion,
	editCity,
	editCountry,
	editRegion,
	getCityList,
	getCityListWithoutPaginaiton,
	getCountryList,
	getLanguagesList,
	getRegionList,
	regionStatus
} from '../apis/regionsAPIs';

interface CommonState {
	countryList: Array<any>;
	regionList: Array<any>;
	cityList: Array<any>;
	regions: Array<any>;
	cities: Array<any>;
	languageList: Array<any>;
	loading: boolean;
	currentPageInCountry: number | string;
	currentPageInRegion: number | string;
	currentPageInCity: number | string;
	countryCount: number;
	regionCount: number;
	cityCount: number;
	listOfCityForDeleteMany: Array<any>;
	filter: any;
}

const initialState: CommonState = {
	countryList: [],
	regionList: [],
	cityList: [],
	listOfCityForDeleteMany: [],
	regions: [],
	cities: [],
	loading: false,
	currentPageInCountry: 1,
	currentPageInRegion: 1,
	currentPageInCity: 1,
	countryCount: 0,
	regionCount: 0,
	languageList: [],
	cityCount: 0,
	filter: null
};

const regionSlice = createSlice({
	name: 'common',
	initialState,
	reducers: {
		handlePaginationInCountry(state, action: any) {
			state.currentPageInCountry = action.payload;
		},
		handlePaginationInRegion(state, action: any) {
			state.currentPageInRegion = action.payload;
		},
		handleFilter(state, action: any) {
			state.filter = action.payload;
		},
		handlePaginationInCity(state, action: any) {
			state.currentPageInCity = action.payload;
		},
		updateCityInDeleteList(state, action: any) {
			const indexOf = state.listOfCityForDeleteMany?.findIndex(
				(item: any) => item.id == action.payload.id
			);
			if (indexOf == -1) {
				state.listOfCityForDeleteMany.push(action.payload);
			} else {
				state.listOfCityForDeleteMany.splice(indexOf, 1);
			}
		},
		addCityArrayInDeleteList(state, action: any) {
			state.listOfCityForDeleteMany = action.payload;
		}
	},
	extraReducers: (builder) => {
		builder.addCase(getCountryList.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(getCountryList.fulfilled, (state, action: any) => {
			state.countryList = action.payload.data.list;
			state.countryCount = action.payload.data.count;
			state.loading = false;
		});
		builder.addCase(getCountryList.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(addCountry.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(addCountry.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(addCountry.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(editCountry.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(editCountry.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(editCountry.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(deleteCountry.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(deleteCountry.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(deleteCountry.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(addRegion.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(addRegion.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(addRegion.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(editRegion.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(editRegion.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(editRegion.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(deleteRegion.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(deleteRegion.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(deleteRegion.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(addCity.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(addCity.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(addCity.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(editCity.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(editCity.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(editCity.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(deleteCity.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(deleteCity.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(deleteCity.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(getLanguagesList.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(
			getLanguagesList.fulfilled,
			(state: CommonState, action: any) => {
				state.loading = false;
				state.languageList = action.payload.data;
			}
		);
		builder.addCase(getLanguagesList.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(getRegionList.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(getRegionList.fulfilled, (state, action: any) => {
			state.regionList = action.payload.data.list;
			state.regionCount = action.payload.data.count;
			state.loading = false;
		});
		builder.addCase(getRegionList.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(getCityList.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(getCityList.fulfilled, (state, action: any) => {
			state.cityList = action.payload.data.list;
			state.cityCount = action.payload.data.count;
			state.loading = false;
		});
		builder.addCase(getCityList.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(
			getCityListWithoutPaginaiton.pending,
			(state: CommonState) => {
				state.loading = true;
			}
		);
		builder.addCase(
			getCityListWithoutPaginaiton.fulfilled,
			(state, action: any) => {
				state.cityList = action.payload.data;
				state.loading = false;
			}
		);
		builder.addCase(
			getCityListWithoutPaginaiton.rejected,
			(state: CommonState) => {
				state.loading = false;
			}
		);
		//change status
		builder.addCase(regionStatus.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(regionStatus.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(regionStatus.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(countryStatus.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(countryStatus.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(countryStatus.rejected, (state: CommonState) => {
			state.loading = false;
		});
		builder.addCase(cityStatus.pending, (state: CommonState) => {
			state.loading = true;
		});
		builder.addCase(cityStatus.fulfilled, (state, action: any) => {
			showSuccess({ message: t(action.payload.message) });
			state.loading = false;
		});
		builder.addCase(cityStatus.rejected, (state: CommonState) => {
			state.loading = false;
		});
	}
});
export const {
	handlePaginationInCountry,
	handlePaginationInRegion,
	handlePaginationInCity,
	updateCityInDeleteList,
	addCityArrayInDeleteList,
	handleFilter
} = regionSlice.actions;
export default regionSlice.reducer;
